import { useState } from 'react';
import { FolderOpen, Loader2, Rocket, Save, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateEvent } from '@/hooks/useEvents';
import type { CreateEventBody } from '@/api/events.api';

/**
 * SorteoForm — form de configuración del minijuego "Sorteo" (familia raffle).
 *
 * Clon 1:1 de la pestaña "Sorteos → + Crear" de fedesuete (ver
 * fede-eventos-general.png): nombre, descripción para el popup, flyer/imagen,
 * depósito mínimo, tipo de premio (Fichas / Bono 100% / Bono 200%), premio,
 * descripción del premio y duración en minutos.
 *
 * Arma `config = { prizeKind, amount, depositMin }` (lo que lee el resolver) y
 * además rellena las columnas planas del Event (`depositMin`, `prizeKind`,
 * `prizeAmount`, `prizeDescription`, `durationMin`) para que la tabla del admin
 * y el widget muestren los datos sin parsear config.
 *
 * Botones del screenshot:
 *   - Guardar Borrador  → status 'draft'
 *   - Crear y Lanzar    → status 'active' (arranca ya; calcula endsAt)
 *   - Guardar como Template → status 'draft' marcado como template en config
 */

type PrizeKind = 'fichas' | 'bono100' | 'bono200';

const PRIZE_OPTIONS: { value: PrizeKind; label: string }[] = [
  { value: 'fichas', label: 'Fichas' },
  { value: 'bono100', label: 'Bono 100%' },
  { value: 'bono200', label: 'Bono 200%' },
];

const PRIZE_NOUN: Record<PrizeKind, string> = {
  fichas: 'Premio en fichas',
  bono100: 'Monto del bono 100%',
  bono200: 'Monto del bono 200%',
};

interface SorteoFormProps {
  /** Callback opcional tras crear/guardar (ej. cerrar el tab o refrescar). */
  onSaved?: () => void;
}

export function SorteoForm({ onSaved }: SorteoFormProps) {
  const createMutation = useCreateEvent();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [flyerUrl, setFlyerUrl] = useState('');
  const [depositMin, setDepositMin] = useState('5000');
  const [prizeKind, setPrizeKind] = useState<PrizeKind>('fichas');
  const [amount, setAmount] = useState('100000');
  const [prizeDescription, setPrizeDescription] = useState('');
  const [durationMin, setDurationMin] = useState('60');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const pending = createMutation.isPending;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'El nombre es requerido';
    if (!depositMin.trim() || Number(depositMin) < 0)
      e.depositMin = 'Depósito mínimo inválido';
    if (!amount.trim() || Number(amount) <= 0)
      e.amount = 'El premio debe ser mayor a 0';
    if (!durationMin.trim() || Number(durationMin) <= 0)
      e.durationMin = 'La duración debe ser mayor a 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function buildBody(
    status: 'draft' | 'active',
    asTemplate = false,
  ): CreateEventBody {
    const amt = Number(amount);
    const dep = Number(depositMin);
    const dur = Number(durationMin);

    const config: Record<string, unknown> = {
      prizeKind,
      amount: amt,
      depositMin: dep,
    };
    if (asTemplate) config.isTemplate = true;

    const body: CreateEventBody = {
      type: 'sorteo',
      name: name.trim(),
      description: description.trim() || undefined,
      flyerUrl: flyerUrl.trim() || undefined,
      depositMin: String(dep),
      prizeKind,
      prizeAmount: String(amt),
      prizeDescription: prizeDescription.trim() || undefined,
      durationMin: dur,
      status,
      config,
    };

    // Crear y Lanzar → arranca ahora y calcula la ventana del evento.
    if (status === 'active') {
      const now = new Date();
      body.startsAt = now.toISOString();
      body.endsAt = new Date(now.getTime() + dur * 60_000).toISOString();
    }
    return body;
  }

  async function submit(status: 'draft' | 'active', asTemplate = false) {
    if (!validate()) return;
    await createMutation.mutateAsync(buildBody(status, asTemplate));
    onSaved?.();
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-amber-500">Crear Nuevo Evento</h2>
        <p className="text-sm text-muted-foreground">Sorteo (raffle) — un ganador al azar al cerrar.</p>
      </div>

      {/* Nombre */}
      <div className="space-y-1.5">
        <Label>
          Nombre del evento <span className="text-red-500">*</span>
        </Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ej: Promo Viernes Dorado"
        />
        {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
      </div>

      {/* Descripción (popup) */}
      <div className="space-y-1.5">
        <Label>Descripcion (para el popup)</Label>
        <textarea
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="ej: Carga $5.000 y participa por $100.000 en fichas!"
        />
      </div>

      {/* Flyer / imagen */}
      <div className="space-y-1.5">
        <Label className="text-base font-semibold">Flyer / imagen del evento</Label>
        <div className="flex gap-2">
          <Input
            value={flyerUrl}
            onChange={(e) => setFlyerUrl(e.target.value)}
            placeholder="https://... o subí una imagen"
          />
          <Button type="button" variant="secondary" size="icon" title="Subir imagen">
            <FolderOpen className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Fila: depósito mínimo · tipo de premio · premio */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label>
            Deposito minimo ($) <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            min="0"
            value={depositMin}
            onChange={(e) => setDepositMin(e.target.value)}
          />
          {errors.depositMin && (
            <p className="text-xs text-red-500">{errors.depositMin}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>
            Tipo de premio <span className="text-red-500">*</span>
          </Label>
          <Select
            value={prizeKind}
            onValueChange={(v) => setPrizeKind(v as PrizeKind)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIZE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>
            {PRIZE_NOUN[prizeKind]} <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {errors.amount && <p className="text-xs text-red-500">{errors.amount}</p>}
        </div>
      </div>

      {/* Fila: descripción del premio · duración */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Descripcion del premio</Label>
          <Input
            value={prizeDescription}
            onChange={(e) => setPrizeDescription(e.target.value)}
            placeholder="$100.000 en fichas"
          />
        </div>

        <div className="space-y-1.5">
          <Label>
            Duracion (minutos) <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            min="1"
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value)}
          />
          {errors.durationMin && (
            <p className="text-xs text-red-500">{errors.durationMin}</p>
          )}
        </div>
      </div>

      {/* Botones */}
      <div className="flex flex-wrap gap-3 pt-2">
        <Button
          type="button"
          variant="secondary"
          disabled={pending}
          onClick={() => submit('draft')}
        >
          {pending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Guardar Borrador
        </Button>

        <Button type="button" disabled={pending} onClick={() => submit('active')}>
          {pending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Rocket className="mr-2 h-4 w-4" />
          )}
          Crear y Lanzar
        </Button>

        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => submit('draft', true)}
        >
          <Package className="mr-2 h-4 w-4" />
          Guardar como Template
        </Button>
      </div>
    </div>
  );
}

export default SorteoForm;
